import type {MouseEventHandler, ReactNode} from "react";
import {useReportedRouteError} from "errors/useReportedRouteError";
import {useMessages} from "i18n";
import {ContentSurface, Prose} from "./ContentPage";

/**
 * A failed content chunk keeps the application's existing chrome and locale because this boundary
 * remains below RootLayout. The separate root fallback supplies its own presentation dependencies
 * when the layout or its providers fail.
 *
 * Like the root fallback, it REPORTS the caught error as well as leaving it in the console
 * (kitecorp/transferify-ui#2581). Its own surface, not the root's: a failed lazy content chunk after a
 * deploy and a provider throwing under the layout need telling apart, and they are the two most common
 * things this app shows an error screen for.
 */
export function ContentRouteError() {
	useReportedRouteError("CONTENT_BOUNDARY");
	const {content} = useMessages();
	return <RouteErrorView heading={content.errorHeading} body={content.errorBody} retry={content.errorRetry}/>;
}

/**
 * Shared error presentation, with no app context or error-object access. Recovery stays a full document
 * load: a router navigation cannot refresh stale chunk hashes. Browser state is read only at render time.
 */
export function RouteErrorView({heading, body, retry, retryHref, onRetry, lang, children}: {
	heading: string;
	body: string;
	retry: string;
	retryHref?: string;
	onRetry?: MouseEventHandler<HTMLAnchorElement>;
	lang?: string;
	children?: ReactNode;
}) {
	return (
		<ContentSurface lang={lang}>
			<Prose>
				{children}
				<h1>{heading}</h1>
				<p>{body}</p>
				<p>
					<a href={retryHref ?? (typeof window === "undefined" ? "/" : window.location.pathname)} onClick={onRetry}>
						{retry}
					</a>
				</p>
			</Prose>
		</ContentSurface>
	);
}
